import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import VideoCall from "@/components/VideoCall";
import { useAuth } from "@/context/AuthContext";
import { Video, CalendarDays, Clock, Phone, IndianRupee, CheckCircle2, Loader2, Stethoscope, FileText } from "lucide-react";

const to12h = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
};
const fmtDate = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : "");
const STATUS = { booked: "bg-blue-50 text-blue-700", completed: "bg-emerald-50 text-emerald-700", cancelled: "bg-red-50 text-red-600" };
const PAY = { pending: "bg-amber-50 text-amber-700", claimed: "bg-sky-50 text-sky-700", confirmed: "bg-emerald-50 text-emerald-700" };

export default function DoctorTeleconsults() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [call, setCall] = useState(null);

  const load = () => api.get("/teleconsults").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const act = async (id, action, okMsg) => {
    try { await api.post(`/teleconsults/${id}/${action}`); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-[#1E5AA8] animate-spin" /></div>;

  const upcoming = items.filter((t) => t.status === "booked");
  const past = items.filter((t) => t.status !== "booked");

  return (
    <div data-testid="doctor-teleconsults">
      <div className="flex items-center gap-2 mb-1"><Video className="w-5 h-5 text-[#1E5AA8]" /><h2 className="font-display text-2xl font-bold text-slate-900">Online Teleconsultations</h2></div>
      <p className="text-slate-500 text-sm mb-5">Patients who booked you online. Join the secure video room at the appointment time.</p>

      {items.length === 0 && (
        <div className="text-center py-16 text-slate-400" data-testid="no-teleconsults">
          No online consultations yet. Make sure teleconsultation is enabled and your availability is set in Profile.
        </div>
      )}

      {upcoming.length > 0 && <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Upcoming</h3>}
      <div className="space-y-3">
        {upcoming.map((t) => (
          <Row key={t.id} t={t} onCall={setCall} user={user} act={act} />
        ))}
      </div>

      {past.length > 0 && <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mt-6 mb-2">Past & cancelled</h3>}
      <div className="space-y-3 opacity-80">
        {past.map((t) => (
          <Row key={t.id} t={t} onCall={setCall} user={user} act={act} />
        ))}
      </div>

      {call && <VideoCall room={call.room} name={call.name} onClose={() => setCall(null)} />}
    </div>
  );
}

function Row({ t, onCall, user, act }) {
  return (
    <div data-testid={`tc-row-${t.id}`} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-slate-900" data-notranslate>{t.patient_name}</p>
          <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${STATUS[t.status] || "bg-slate-100"}`}>{t.status}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${PAY[t.payment_status] || "bg-slate-100"}`}>{t.payment_status === "pending" ? "Payment due" : t.payment_status === "claimed" ? "Patient says paid" : "Paid"}</span>
        </div>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><CalendarDays className="w-4 h-4" />{fmtDate(t.date)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{to12h(t.time)}</span>
          {t.specialty && <span className="inline-flex items-center gap-1"><Stethoscope className="w-4 h-4" />{t.specialty}</span>}
          {t.fee ? <span className="inline-flex items-center"><IndianRupee className="w-3.5 h-3.5" data-notranslate />{t.fee}</span> : null}
          {t.patient_phone && <a href={`tel:${t.patient_phone}`} className="inline-flex items-center gap-1 text-[#1E5AA8]"><Phone className="w-4 h-4" data-notranslate />{t.patient_phone}</a>}
        </p>
        {t.reason && <p className="text-sm text-slate-600 mt-1" data-notranslate>Reason: {t.reason}</p>}
        {(t.reports || []).length > 0 && (
          <div className="mt-2" data-testid={`tc-reports-${t.id}`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Patient reports</p>
            <div className="flex flex-wrap gap-1.5">
              {(t.reports || []).map((rep) => (
                <a key={rep.file_id} href={`${API}/teleconsults/${t.id}/reports/${rep.file_id}`} target="_blank" rel="noreferrer" data-testid="tc-report-link"
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-[#EBF3FB] text-[#1E5AA8] hover:bg-blue-100" data-notranslate>
                  <FileText className="w-3 h-3" /> {rep.filename}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      {t.status === "booked" && (
        <div className="flex items-center gap-2 flex-wrap">
          {t.payment_status !== "confirmed" && (
            <button data-testid={`tc-confirm-pay-${t.id}`} onClick={() => act(t.id, "confirm-payment", "Payment confirmed")} className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-xl"><CheckCircle2 className="w-4 h-4" /> Confirm payment</button>
          )}
          <button data-testid={`tc-join-${t.id}`} onClick={() => onCall({ room: t.room, name: "Dr. " + (user?.full_name || user?.name || "Doctor") })} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-[#1E5AA8] hover:bg-[#123C73] px-3 py-2 rounded-xl"><Video className="w-4 h-4" /> Join video</button>
          <button data-testid={`tc-complete-${t.id}`} onClick={() => act(t.id, "complete", "Marked complete")} className="text-sm text-slate-500 hover:text-slate-800 px-2 py-2">Complete</button>
        </div>
      )}
    </div>
  );
}
